import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FikoFloatingToolbarComponent } from './fiko-floating-toolbar.component';

/**
 * 🎯 FikoFloatingToolbarService
 *
 * Birden fazla floating toolbar instance'ını yönetmek için servis
 *
 * Özellikler:
 * - ✅ Instance registry - Tüm toolbar'ları takip et
 * - ✅ Show/Hide all - Toplu görünürlük kontrolü
 * - ✅ Find by ID - ID ile toolbar bul
 * - ✅ Position management - Pozisyon kaydetme/yükleme
 * - ✅ State persistence - localStorage ile durum saklama
 * - ✅ Observable updates - Reactive updates
 *
 * Kullanım:
 * ```typescript
 * constructor(private toolbarService: FikoFloatingToolbarService) {}
 *
 * // Toolbar kaydet
 * this.toolbarService.kaydet('mainTools', this.toolbarRef);
 *
 * // ID ile bul
 * const toolbar = this.toolbarService.bul('mainTools');
 * toolbar?.ac();
 *
 * // Tümünü gizle
 * this.toolbarService.tumunuGizle();
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class FikoFloatingToolbarService {

  /** Toolbar registry - ID ile instance map */
  private toolbars: Map<string, FikoFloatingToolbarComponent> = new Map();

  /** Toolbar durumları - Observable */
  private toolbarStates$ = new BehaviorSubject<Map<string, ToolbarState>>(new Map());

  /** LocalStorage key prefix */
  private readonly STORAGE_PREFIX = 'fikoyt_toolbar_';

  /** Z-index yönetimi - Global tüm toolbarlar için */
  private readonly TEMEL_Z_INDEX = 1000;
  private readonly Z_INDEX_ARTIS = 1; // Her tıklamada 1 artır
  private mevcutZIndex = this.TEMEL_Z_INDEX;
  private toolbarZIndices: Map<FikoFloatingToolbarComponent, number> = new Map();

  /** Z-index sıralama - En üstteki toolbar'ı takip et */
  private zIndexSirasi: FikoFloatingToolbarComponent[] = [];

  constructor() {
    // Sayfa yüklendiğinde kaydedilmiş pozisyonları yükle
    this.yukleKaydedilmisPozisyonlar();
  }

  /**
   * Toolbar instance'ı kaydet
   */
  kaydet(id: string, toolbar: FikoFloatingToolbarComponent): void {
    this.toolbars.set(id, toolbar);

    // Z-index ata (yeni açılan toolbar en üstte)
    const yeniZIndex = this.toolbarAcildi(toolbar);
    toolbar.zIndex = yeniZIndex;

    // State güncelle
    const states = this.toolbarStates$.value;
    states.set(id, {
      id,
      gorunur: toolbar.gorunur,
      minimize: toolbar.minimize,
      pozisyon: toolbar.pozisyonAl()
    });
    this.toolbarStates$.next(states);

    // Kaydedilmiş pozisyon varsa uygula
    const kaydedilmis = this.pozisyonYukle(id);
    if (kaydedilmis?.pozisyon) {
      setTimeout(() => {
        toolbar.pozisyonAyarla(kaydedilmis.pozisyon!);
      }, 200);
    }
  }

  /**
   * Toolbar instance'ı sil
   */
  sil(id: string): void {
    this.toolbars.delete(id);

    const states = this.toolbarStates$.value;
    states.delete(id);
    this.toolbarStates$.next(states);
  }

  /**
   * ID ile toolbar bul
   */
  bul(id: string): FikoFloatingToolbarComponent | undefined {
    return this.toolbars.get(id);
  }

  /**
   * Tüm toolbar'ları al
   */
  tumunuAl(): FikoFloatingToolbarComponent[] {
    return Array.from(this.toolbars.values());
  }

  /**
   * Tüm toolbar ID'lerini al
   */
  tumIdleriAl(): string[] {
    return Array.from(this.toolbars.keys());
  }

  /**
   * Toolbar var mı kontrol et
   */
  varMi(id: string): boolean {
    return this.toolbars.has(id);
  }

  /**
   * Tüm toolbar'ları göster
   */
  tumunuGoster(): void {
    this.toolbars.forEach(toolbar => {
      if (!toolbar.miAcik) {
        toolbar.ac();
      }
    });
  }

  /**
   * Tüm toolbar'ları gizle
   */
  tumunuGizle(): void {
    this.toolbars.forEach(toolbar => {
      if (toolbar.miAcik) {
        toolbar.kapa();
      }
    });
  }

  /**
   * Belirli bir toolbar'ı göster
   */
  goster(id: string): void {
    const toolbar = this.bul(id);
    if (toolbar && !toolbar.miAcik) {
      toolbar.ac();
    }
  }

  /**
   * Belirli bir toolbar'ı gizle
   */
  gizle(id: string): void {
    const toolbar = this.bul(id);
    if (toolbar && toolbar.miAcik) {
      toolbar.kapa();
    }
  }

  /**
   * Toolbar görünürlüğünü toggle et
   */
  toggle(id: string): void {
    const toolbar = this.bul(id);
    if (toolbar) {
      toolbar.toggle();
    }
  }

  /**
   * Toolbar'ları cascade düzenle (birbiri üzerine gelmesin)
   */
  cascadeDuzenle(baslangicX: number = 50, baslangicY: number = 50, offset: number = 30): void {
    let currentX = baslangicX;
    let currentY = baslangicY;

    this.toolbars.forEach(toolbar => {
      toolbar.pozisyonAyarla({ top: currentY, left: currentX });
      currentX += offset;
      currentY += offset;
    });
  }

  /**
   * Toolbar'ları grid düzenle
   */
  gridDuzenle(columns: number = 3, baslangicX: number = 50, baslangicY: number = 50, gapX: number = 220, gapY: number = 60): void {
    let index = 0;
    this.toolbars.forEach(toolbar => {
      const row = Math.floor(index / columns);
      const col = index % columns;

      toolbar.pozisyonAyarla({
        top: baslangicY + (row * gapY),
        left: baslangicX + (col * gapX)
      });

      index++;
    });
  }

  /**
   * Tüm toolbar'ları merkeze hizala
   */
  tumunuOrtala(): void {
    this.toolbars.forEach(toolbar => {
      toolbar.ortala();
    });
  }

  /**
   * Toolbar durumlarını Observable olarak al
   */
  durumlar$(): Observable<Map<string, ToolbarState>> {
    return this.toolbarStates$.asObservable();
  }

  /**
   * Toolbar pozisyonunu localStorage'a kaydet
   */
  pozisyonKaydet(id: string): void {
    const toolbar = this.bul(id);
    if (toolbar) {
      const state: ToolbarState = {
        id,
        gorunur: toolbar.miAcik,
        minimize: toolbar.minimize,
        pozisyon: toolbar.pozisyonAl()
      };

      localStorage.setItem(
        this.STORAGE_PREFIX + id,
        JSON.stringify(state)
      );
    }
  }

  /**
   * Toolbar pozisyonunu localStorage'dan yükle
   */
  pozisyonYukle(id: string): ToolbarState | null {
    const stored = localStorage.getItem(this.STORAGE_PREFIX + id);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  /**
   * Tüm toolbar pozisyonlarını kaydet
   */
  tumPozisyonlariKaydet(): void {
    this.toolbars.forEach((toolbar, id) => {
      this.pozisyonKaydet(id);
    });
  }

  /**
   * Kaydedilmiş pozisyonları yükle
   */
  private yukleKaydedilmisPozisyonlar(): void {
    // LocalStorage'daki tüm toolbar kayıtlarını bul
    const keys = Object.keys(localStorage).filter(key =>
      key.startsWith(this.STORAGE_PREFIX)
    );

    keys.forEach(key => {
      const id = key.replace(this.STORAGE_PREFIX, '');
      const state = this.pozisyonYukle(id);

      if (state) {
        const states = this.toolbarStates$.value;
        states.set(id, state);
        this.toolbarStates$.next(states);
      }
    });
  }

  /**
   * Tüm kaydedilmiş pozisyonları temizle
   */
  tumKayitlariTemizle(): void {
    const keys = Object.keys(localStorage).filter(key =>
      key.startsWith(this.STORAGE_PREFIX)
    );

    keys.forEach(key => localStorage.removeItem(key));
  }

  /**
   * Belirli bir toolbar kaydını sil
   */
  kayitSil(id: string): void {
    localStorage.removeItem(this.STORAGE_PREFIX + id);
  }

  /**
   * İstatistikler
   */
  istatistikler(): ToolbarStatistics {
    const toolbars = this.tumunuAl();
    return {
      toplam: toolbars.length,
      gorunur: toolbars.filter(t => t.miAcik).length,
      gizli: toolbars.filter(t => !t.miAcik).length,
      minimize: toolbars.filter(t => t.minimize).length
    };
  }

  /**
   * Tüm toolbar'ları kapat ve temizle
   */
  tumunuKapat(): void {
    this.toolbars.forEach(toolbar => {
      toolbar.ngOnDestroy();
    });
    this.toolbars.clear();
    this.toolbarStates$.next(new Map());
    this.toolbarZIndices.clear();
  }

  /**
   * 🎯 Toolbar açıldığında z-index ata
   * Global z-index yönetimi - Her yeni toolbar en üstte açılır
   */
  toolbarAcildi(toolbar: FikoFloatingToolbarComponent): number {
    // Mevcut z-index'i artır
    this.mevcutZIndex += this.Z_INDEX_ARTIS;
    const yeniZIndex = this.mevcutZIndex;

    // Toolbar'ı z-index map'ine ekle
    this.toolbarZIndices.set(toolbar, yeniZIndex);

    // Sıralama listesine ekle (en sonda = en üstte)
    this.zIndexSirasi = this.zIndexSirasi.filter(t => t !== toolbar); // Önce kaldır
    this.zIndexSirasi.push(toolbar); // Sonra en sona ekle

    return yeniZIndex;
  }

  /**
   * 🎯 Toolbar tıklandığında z-index güncelle (öne getir)
   * Click-to-front: Tıklanan toolbar en üste gelir
   */
  toolbarTiklandi(toolbar: FikoFloatingToolbarComponent): number {
    const mevcutZIndex = this.toolbarZIndices.get(toolbar);

    // Eğer zaten en üstteyse, z-index'i artırma (gereksiz)
    if (this.zIndexSirasi.length > 0 && this.zIndexSirasi[this.zIndexSirasi.length - 1] === toolbar) {
      return mevcutZIndex || this.mevcutZIndex;
    }

    // Toolbar henüz kayıtlı değilse, yeni z-index ata
    if (!mevcutZIndex) {
      return this.toolbarAcildi(toolbar);
    }

    // Yeni z-index hesapla ve güncelle
    this.mevcutZIndex += this.Z_INDEX_ARTIS;
    const yeniZIndex = this.mevcutZIndex;
    this.toolbarZIndices.set(toolbar, yeniZIndex);

    // Sıralama listesini güncelle (tıklanan toolbar en sona)
    this.zIndexSirasi = this.zIndexSirasi.filter(t => t !== toolbar);
    this.zIndexSirasi.push(toolbar);

    return yeniZIndex;
  }

  /**
   * 🎯 Toolbar kapandığında z-index temizle
   */
  toolbarKapandi(toolbar: FikoFloatingToolbarComponent): void {
    this.toolbarZIndices.delete(toolbar);
    this.zIndexSirasi = this.zIndexSirasi.filter(t => t !== toolbar);
  }

  /**
   * 🎯 En üstteki toolbar'ı al
   */
  enUsttekiToolbar(): FikoFloatingToolbarComponent | null {
    return this.zIndexSirasi.length > 0 ? this.zIndexSirasi[this.zIndexSirasi.length - 1] : null;
  }

  /**
   * 🎯 Toolbar'ın z-index sıralamasını al (0 = en altta, N-1 = en üstte)
   */
  toolbarSirasi(toolbar: FikoFloatingToolbarComponent): number {
    return this.zIndexSirasi.indexOf(toolbar);
  }

  /**
   * 🎯 Mevcut en yüksek z-index'i al
   */
  maxZIndex(): number {
    return this.mevcutZIndex;
  }

  /**
   * 🎯 Tüm z-index'leri sıfırla (reset)
   */
  zIndexSifirla(): void {
    this.mevcutZIndex = this.TEMEL_Z_INDEX;
    this.toolbarZIndices.clear();
    this.zIndexSirasi = [];

    // Tüm toolbar'ları yeniden indexle
    let index = this.TEMEL_Z_INDEX;
    this.toolbars.forEach(toolbar => {
      index += this.Z_INDEX_ARTIS;
      toolbar.zIndex = index;
      this.toolbarZIndices.set(toolbar, index);
      this.zIndexSirasi.push(toolbar);
    });

    this.mevcutZIndex = index;
  }
}

/**
 * Toolbar durumu interface
 */
export interface ToolbarState {
  id: string;
  gorunur: boolean;
  minimize: boolean;
  pozisyon?: { top: number; left: number };
}

/**
 * İstatistik interface
 */
export interface ToolbarStatistics {
  toplam: number;
  gorunur: number;
  gizli: number;
  minimize: number;
}
